import {
  createPublicClient,
  formatUnits,
  getAddress,
  http,
  parseAbiItem,
} from "viem";
import { mainnet } from "viem/chains";
import { multicall } from "viem/actions";
import { traitSpinAbi } from "./lib/abis.js";
import {
  getMainnetRpcProxyAbsoluteUrl,
  getStakingEnv,
  isHexAddress,
  isXpLeaderboardEnabledForHost,
} from "./lib/env.js";
import { getLogsChunked } from "./lib/stakingData.js";
import {
  advanceTraitLayerImageOnError,
  buildTraitLayerImageCandidates,
  cloudflareTraitLayerImageUrl,
  encodeTraitImageCandidatesForDom,
  TRAIT_IMAGE_MISSING_DATA_URI,
} from "./lib/traitAssetUrl.js";
import { isRecentTraitUnlocked } from "./stakingSettings.js";

const transferEvent = parseAbiItem(
  "event Transfer(address indexed from, address indexed to, uint256 value)"
);

const traitGrantedEvent = parseAbiItem(
  "event TraitGranted(address indexed user, uint256 indexed traitTypeId, uint256 indexed layerIndex, bytes32 traitId, string traitName, uint256 requestBlock)"
);

const ZERO_ADDR = "0x0000000000000000000000000000000000000000";

const TOP_N = 10;

function leaderboardTransportUrls() {
  const proxy = getMainnetRpcProxyAbsoluteUrl();
  const list = [
    ...(proxy ? [proxy] : []),
    "https://ethereum.publicnode.com",
    "https://cloudflare-eth.com",
    "https://eth.llamarpc.com",
  ];
  const seen = new Set();
  return list.filter((u) => {
    const k = u.toLowerCase();
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}

function leaderboardClients() {
  return leaderboardTransportUrls().map((url) =>
    createPublicClient({
      chain: mainnet,
      transport: http(url, { batch: false, timeout: 60_000 }),
    })
  );
}

/** @param {import('viem').Log[]} logs */
function dedupeLogs(logs) {
  const map = new Map();
  for (const log of logs) {
    const k = `${log.transactionHash}-${log.logIndex}`;
    if (!map.has(k)) map.set(k, log);
  }
  return [...map.values()];
}

function xpTransferScanFromBlock() {
  const rawXp = String(import.meta.env.VITE_OWLEX_XP_DEPLOY_BLOCK ?? "").trim();
  if (rawXp) {
    try {
      return BigInt(rawXp);
    } catch {
      /* fall through */
    }
  }
  const rawStaking = String(import.meta.env.VITE_STAKING_DEPLOY_BLOCK ?? "").trim();
  if (rawStaking) {
    try {
      return BigInt(rawStaking);
    } catch {
      /* fall through */
    }
  }
  return 24892219n;
}

function traitSpinDeployBlockFromEnv() {
  const raw = String(import.meta.env.VITE_TRAIT_SPIN_DEPLOY_BLOCK ?? "").trim();
  if (!raw) return null;
  try {
    return BigInt(raw);
  } catch {
    return null;
  }
}

function lookbackBlocksFromEnv() {
  const raw = String(import.meta.env.VITE_LEADERBOARD_TRAIT_LOOKBACK_BLOCKS ?? "").trim();
  if (!raw) return 450_000n;
  try {
    const n = BigInt(raw);
    return n > 0n ? n : 450_000n;
  } catch {
    return 450_000n;
  }
}

/**
 * @param {import('viem').PublicClient} client
 * @param {bigint} fromBlock
 * @param {`0x${string}`} xpAddr
 */
async function fetchXpTransferLogs(client, fromBlock, xpAddr) {
  const latestBlock = await client.getBlockNumber();
  if (fromBlock > latestBlock) return [];
  return getLogsChunked(
    client,
    {
      address: xpAddr,
      event: transferEvent,
      fromBlock,
      toBlock: "latest",
    },
    { latestBlock }
  );
}

/**
 * @param {import('viem').PublicClient} client
 * @param {bigint} fromBlock
 * @param {`0x${string}`} spinAddr
 */
async function fetchTraitGrantedLogs(client, fromBlock, spinAddr) {
  const latestBlock = await client.getBlockNumber();
  if (fromBlock > latestBlock) return [];
  return getLogsChunked(
    client,
    {
      address: spinAddr,
      event: traitGrantedEvent,
      fromBlock,
      toBlock: "latest",
    },
    { latestBlock }
  );
}

async function fetchMergedXpTransferLogs(xpAddr, fromBlock) {
  const clients = leaderboardClients();
  const results = await Promise.allSettled(
    clients.map((c) => fetchXpTransferLogs(c, fromBlock, xpAddr))
  );
  /** @type {import('viem').Log[]} */
  const merged = [];
  for (const r of results) {
    if (r.status === "fulfilled") merged.push(...r.value);
    else console.warn("[owlex-staking] XP Transfer logs (one RPC failed)", r.reason);
  }
  return dedupeLogs(merged);
}

async function fetchMergedTraitGrantedLogs(spinAddr) {
  const readClient = leaderboardClients()[0];
  const tip = await readClient.getBlockNumber();
  const lookback = lookbackBlocksFromEnv();
  const deploy = traitSpinDeployBlockFromEnv();
  let fromBlock = tip > lookback ? tip - lookback : 0n;
  if (deploy != null && deploy > fromBlock) fromBlock = deploy;

  const clients = leaderboardClients();
  const results = await Promise.allSettled(
    clients.map((c) => fetchTraitGrantedLogs(c, fromBlock, spinAddr))
  );
  /** @type {import('viem').Log[]} */
  const merged = [];
  for (const r of results) {
    if (r.status === "fulfilled") merged.push(...r.value);
    else console.warn("[owlex-staking] TraitGranted logs (one RPC failed)", r.reason);
  }
  return dedupeLogs(merged);
}

function balancesFromTransferReplay(logs) {
  /** @type {Map<string, bigint>} */
  const bal = new Map();
  const touch = (addr, delta) => {
    const k = addr.toLowerCase();
    bal.set(k, (bal.get(k) ?? 0n) + delta);
  };
  for (const log of logs) {
    const from = log.args?.from;
    const to = log.args?.to;
    const v = log.args?.value;
    if (typeof from !== "string" || typeof to !== "string" || typeof v !== "bigint") continue;
    if (from !== ZERO_ADDR) touch(from, -v);
    if (to !== ZERO_ADDR) touch(to, v);
  }
  return bal;
}

function topXpHolders(balances) {
  const entries = [...balances.entries()].filter(([, b]) => b > 0n);
  entries.sort((a, b) => {
    if (b[1] > a[1]) return 1;
    if (b[1] < a[1]) return -1;
    return 0;
  });
  return entries.slice(0, TOP_N).map(([addr, xpWei]) => ({
    user: getAddress(/** @type {`0x${string}`} */ (addr)),
    xpWei,
  }));
}

/** @param {import('viem').Log[]} logs */
function latestTraitGrantByUser(logs) {
  /** @type {Map<string, import('viem').Log>} */
  const m = new Map();
  for (const log of logs) {
    const user = log.args?.user;
    if (typeof user !== "string") continue;
    const a = user.toLowerCase();
    const prev = m.get(a);
    const bn = log.blockNumber ?? 0n;
    const li = log.logIndex ?? 0n;
    if (
      !prev ||
      bn > (prev.blockNumber ?? 0n) ||
      (bn === (prev.blockNumber ?? 0n) && li > (prev.logIndex ?? 0n))
    ) {
      m.set(a, log);
    }
  }
  return m;
}

async function firstReadClient() {
  for (const url of leaderboardTransportUrls()) {
    try {
      const c = createPublicClient({
        chain: mainnet,
        transport: http(url, { batch: true, timeout: 30_000 }),
      });
      await c.getChainId();
      return c;
    } catch {
      /* next */
    }
  }
  throw new Error("No RPC");
}

/**
 * @param {import('viem').PublicClient} client
 * @param {`0x${string}`} spinAddr
 * @param {{ user: `0x${string}`, xpWei: bigint }[]} top
 * @param {Map<string, import('viem').Log>} traitByUser
 */
async function enrichTraitRows(client, spinAddr, top, traitByUser) {
  const traitRows = top.filter((row) => {
    const log = traitByUser.get(row.user.toLowerCase());
    return (
      log != null &&
      log.args?.traitTypeId != null &&
      log.args?.layerIndex != null &&
      typeof log.args?.traitName === "string"
    );
  });

  const contracts = traitRows.flatMap((row) => {
    const log = traitByUser.get(row.user.toLowerCase());
    const tid = log?.args?.traitTypeId;
    const lid = log?.args?.layerIndex;
    if (tid == null || lid == null) return [];
    return [
      {
        address: spinAddr,
        abi: traitSpinAbi,
        functionName: "traitTypeName",
        args: [tid],
      },
      {
        address: spinAddr,
        abi: traitSpinAbi,
        functionName: "layerCloudflareId",
        args: [tid, lid],
      },
    ];
  });

  const metaRes =
    contracts.length > 0
      ? await multicall(client, { contracts, allowFailure: true })
      : [];

  /** @type {Map<string, { categoryName: string; cfId: string }>} */
  const traitMeta = new Map();
  for (let i = 0; i < traitRows.length; i++) {
    const row = traitRows[i];
    const nameRes = metaRes[i * 2];
    const cfRes = metaRes[i * 2 + 1];
    let categoryName = "Trait";
    if (nameRes?.status === "success" && typeof nameRes.result === "string" && nameRes.result) {
      categoryName = nameRes.result;
    }
    let cfId = "";
    if (cfRes?.status === "success" && typeof cfRes.result === "string") cfId = cfRes.result;
    traitMeta.set(row.user.toLowerCase(), { categoryName, cfId });
  }

  /** @type {{ rank: number, user: `0x${string}`, xpWei: bigint, categoryName: string, traitName: string, imageCandidates: string[] }[]} */
  const out = [];
  for (let i = 0; i < top.length; i++) {
    const row = top[i];
    const log = traitByUser.get(row.user.toLowerCase());
    const tm = traitMeta.get(row.user.toLowerCase());

    let categoryName = "—";
    let traitName = "—";
    /** @type {string[]} */
    let imageCandidates = [];

    if (log && typeof log.args?.traitName === "string" && log.args.traitName) {
      traitName = log.args.traitName;
    }

    if (tm && traitName !== "—") {
      categoryName = tm.categoryName;
      const cfUrl = cloudflareTraitLayerImageUrl(tm.cfId);
      if (cfUrl) imageCandidates.push(cfUrl);
      for (const u of buildTraitLayerImageCandidates(tm.categoryName, traitName)) {
        if (!imageCandidates.includes(u)) imageCandidates.push(u);
      }
    } else if (traitName !== "—") {
      categoryName = "Trait";
      imageCandidates = buildTraitLayerImageCandidates(categoryName, traitName);
    }
    if (imageCandidates.length === 0) imageCandidates = [TRAIT_IMAGE_MISSING_DATA_URI];

    out.push({
      rank: i + 1,
      user: row.user,
      xpWei: row.xpWei,
      categoryName,
      traitName,
      imageCandidates,
    });
  }
  return out;
}

function formatAddr(a) {
  const s = String(a);
  if (s.length < 10) return s;
  return `${s.slice(0, 6)}…${s.slice(-4)}`;
}

function escapeAttr(s) {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;");
}

function escapeHtml(s) {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * @param {{ rank: number, user: `0x${string}`, xpWei: bigint }} row
 */
function rowHtmlSimple(row) {
  const xpStr = formatXp(row.xpWei);
  return `
    <li class="leaderboard__row leaderboard__row--simple">
      <span class="leaderboard__rank" aria-hidden="true">${row.rank}</span>
      <div class="leaderboard__user">
        <span class="leaderboard__addr" title="${escapeAttr(row.user)}">${escapeHtml(formatAddr(row.user))}</span>
        <a class="leaderboard__etherscan" href="https://etherscan.io/address/${escapeAttr(row.user)}" target="_blank" rel="noopener noreferrer">Etherscan</a>
      </div>
      <div class="leaderboard__xp">
        <img class="leaderboard__xp-icon" src="/xp.png" alt="" width="22" height="22" decoding="async" />
        <span class="leaderboard__xp-val">${escapeHtml(xpStr)}</span>
      </div>
    </li>`;
}

/**
 * @param {{ rank: number, user: `0x${string}`, xpWei: bigint, categoryName: string, traitName: string, imageCandidates: string[] }} row
 */
function rowHtmlWithTrait(row) {
  const xpStr = formatXp(row.xpWei);
  const candidates =
    row.imageCandidates.length > 0 ? row.imageCandidates : [TRAIT_IMAGE_MISSING_DATA_URI];
  const cf = candidates[0] || TRAIT_IMAGE_MISSING_DATA_URI;
  const urlsAttr = escapeAttr(encodeTraitImageCandidatesForDom(candidates));
  return `
    <li class="leaderboard__row">
      <span class="leaderboard__rank" aria-hidden="true">${row.rank}</span>
      <div class="leaderboard__user">
        <span class="leaderboard__addr" title="${escapeAttr(row.user)}">${escapeHtml(formatAddr(row.user))}</span>
        <a class="leaderboard__etherscan" href="https://etherscan.io/address/${escapeAttr(row.user)}" target="_blank" rel="noopener noreferrer">Etherscan</a>
      </div>
      <div class="leaderboard__xp">
        <img class="leaderboard__xp-icon" src="/xp.png" alt="" width="22" height="22" decoding="async" />
        <span class="leaderboard__xp-val">${escapeHtml(xpStr)}</span>
      </div>
      <div class="leaderboard__trait">
        <div class="leaderboard__trait-visual">
          <img
            class="leaderboard__trait-img"
            src="${escapeAttr(cf)}"
            data-trait-img-urls="${urlsAttr}"
            data-trait-img-i="0"
            alt=""
            width="56"
            height="56"
            loading="lazy"
            decoding="async"
          />
        </div>
        <div class="leaderboard__trait-text">
          <span class="leaderboard__trait-cat">${escapeHtml(row.categoryName)}</span>
          <span class="leaderboard__trait-name">${escapeHtml(row.traitName)}</span>
        </div>
      </div>
    </li>`;
}

/** @param {bigint} xpWei */
function formatXp(xpWei) {
  try {
    const s = formatUnits(xpWei, 18);
    const n = Number.parseFloat(s);
    if (!Number.isFinite(n)) return s;
    return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
  } catch {
    return "—";
  }
}

/** @type {string | null} */
let leaderboardListHtmlCache = null;
/** @type {boolean | null} */
let leaderboardCacheWasTrait = null;
let leaderboardLoadGeneration = 0;

function bindLeaderboardImages(root) {
  if (root.dataset.leaderboardImgDelegation === "1") return;
  root.dataset.leaderboardImgDelegation = "1";
  root.addEventListener(
    "error",
    (e) => {
      const t = e.target;
      if (!(t instanceof HTMLImageElement) || !t.classList.contains("leaderboard__trait-img")) return;
      if (advanceTraitLayerImageOnError(t)) return;
      if (t.dataset.triedPlaceholder !== "1") {
        t.dataset.triedPlaceholder = "1";
        t.src = TRAIT_IMAGE_MISSING_DATA_URI;
      }
    },
    true
  );
}

/**
 * @param {{ force?: boolean }} [options]
 */
async function loadXpLeaderboardData(options = {}) {
  if (!isXpLeaderboardEnabledForHost()) {
    closeXpLeaderboardModal();
    return;
  }

  const root = document.getElementById("xp-leaderboard-modal");
  const listEl = document.getElementById("xp-leaderboard-list");
  const loading = document.getElementById("xp-leaderboard-loading");
  const errEl = document.getElementById("xp-leaderboard-error");
  const refreshBtn = document.getElementById("xp-leaderboard-refresh");
  if (!root || !listEl || !loading || !errEl) return;

  const env = getStakingEnv();
  const xp = env.owlexXp;
  const spin = env.traitSpinContract;
  if (!isHexAddress(xp)) return;

  const wantTrait = isRecentTraitUnlocked() && isHexAddress(spin);
  const spinAddr = wantTrait ? /** @type {`0x${string}`} */ (spin) : null;

  const force = options.force === true;
  if (
    !force &&
    leaderboardListHtmlCache != null &&
    leaderboardCacheWasTrait === wantTrait
  ) {
    listEl.innerHTML = leaderboardListHtmlCache;
    listEl.hidden = false;
    loading.hidden = true;
    errEl.hidden = true;
    if (refreshBtn) refreshBtn.disabled = false;
    if (wantTrait) bindLeaderboardImages(root);
    return;
  }

  const gen = ++leaderboardLoadGeneration;
  if (refreshBtn) refreshBtn.disabled = true;
  loading.hidden = false;
  listEl.hidden = true;
  errEl.hidden = true;
  errEl.textContent = "";

  try {
    const fromBlock = xpTransferScanFromBlock();
    const xpPromise = fetchMergedXpTransferLogs(/** @type {`0x${string}`} */ (xp), fromBlock);
    const traitPromise =
      wantTrait && spinAddr ? fetchMergedTraitGrantedLogs(spinAddr) : Promise.resolve([]);

    const [xferLogs, traitLogs] = await Promise.all([xpPromise, traitPromise]);
    if (gen !== leaderboardLoadGeneration) return;

    const balances = balancesFromTransferReplay(xferLogs);
    const top = topXpHolders(balances);
    if (top.length === 0) {
      leaderboardListHtmlCache = null;
      leaderboardCacheWasTrait = null;
      loading.hidden = true;
      errEl.hidden = false;
      errEl.textContent =
        "No OWXP balances found from on-chain transfers. Check VITE_OWLEX_XP_DEPLOY_BLOCK (or VITE_STAKING_DEPLOY_BLOCK).";
      if (refreshBtn) refreshBtn.disabled = false;
      return;
    }

    let html = "";
    if (wantTrait && spinAddr) {
      const traitByUser = latestTraitGrantByUser(traitLogs);
      const client = await firstReadClient();
      const rows = await enrichTraitRows(client, spinAddr, top, traitByUser);
      if (gen !== leaderboardLoadGeneration) return;
      html = rows.map((r) => rowHtmlWithTrait(r)).join("");
      bindLeaderboardImages(root);
    } else {
      html = top.map((r, i) => rowHtmlSimple({ rank: i + 1, user: r.user, xpWei: r.xpWei })).join("");
    }

    listEl.innerHTML = html;
    leaderboardListHtmlCache = html;
    leaderboardCacheWasTrait = wantTrait;
    loading.hidden = true;
    listEl.hidden = false;
  } catch (e) {
    console.error("[owlex-staking] leaderboard", e);
    if (gen !== leaderboardLoadGeneration) return;
    loading.hidden = true;
    errEl.hidden = false;
    errEl.textContent = "Could not load leaderboard. Check RPC and try again.";
  } finally {
    if (gen === leaderboardLoadGeneration && refreshBtn) refreshBtn.disabled = false;
  }
}

function closeXpLeaderboardModal() {
  const modal = document.getElementById("xp-leaderboard-modal");
  if (!modal) return;
  modal.hidden = true;
  document.body.classList.remove("xp-leaderboard-modal-open");
  leaderboardLoadGeneration += 1;
}

function openXpLeaderboardModal() {
  if (!isXpLeaderboardEnabledForHost()) return;
  const modal = document.getElementById("xp-leaderboard-modal");
  if (!modal) return;
  modal.hidden = false;
  document.body.classList.add("xp-leaderboard-modal-open");
  void loadXpLeaderboardData({ force: false });
}

export function initXpLeaderboard() {
  const btn = document.getElementById("btn-xp-leaderboard");
  const modal = document.getElementById("xp-leaderboard-modal");
  const closeBtn = document.getElementById("xp-leaderboard-close");
  const refreshBtn = document.getElementById("xp-leaderboard-refresh");
  if (!btn || !modal) return;

  if (!isXpLeaderboardEnabledForHost()) {
    btn.hidden = true;
    modal.hidden = true;
    return;
  }

  const env = getStakingEnv();
  btn.hidden = !isHexAddress(env.owlexXp);

  window.addEventListener("owlex:leaderboard-settings", () => {
    leaderboardListHtmlCache = null;
    leaderboardCacheWasTrait = null;
    if (!modal.hidden) void loadXpLeaderboardData({ force: true });
  });

  btn.addEventListener("click", () => openXpLeaderboardModal());
  closeBtn?.addEventListener("click", () => closeXpLeaderboardModal());
  refreshBtn?.addEventListener("click", () => {
    leaderboardListHtmlCache = null;
    leaderboardCacheWasTrait = null;
    void loadXpLeaderboardData({ force: true });
  });

  modal.addEventListener("click", (e) => {
    const t = e.target;
    if (t instanceof HTMLElement && t.dataset.xpLbClose === "1") closeXpLeaderboardModal();
  });

  window.addEventListener("keydown", (e) => {
    if (e.key !== "Escape") return;
    if (modal.hidden) return;
    closeXpLeaderboardModal();
  });
}
